import {
  Injectable,
  PLATFORM_ID,
  PendingTasks,
  InjectionToken,
  inject,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ProductContractedRow {
  id: number;
  cliente_id: number;
  producto_id: number;
  tipo_producto_id: number;
  category_id: number;
  precio: number;
  total_amount: number | null;
  tax_rate: number | null;
  cantidad: number;
  concepto: string | null;
  billing_frequency: 'mensual' | 'trimestral' | 'anual' | 'unico';
  activo: number;
  is_suspended: number;
  is_cancelled: number;
  caducado: number;
  auto_renovar: number;
  payment_failed: number;
  fecini: string | null;
  fecfin: string | null;
  next_billing_date: string | null;
  last_billing_date: string | null;
  currency: string;
  notes_subscription: string | null;
  unit_access_code: string | null;
}

interface ProductsContractedResponse {
  type: string;
  msg: string;
  data: ProductContractedRow[];
}

/**
 * Token del servidor (server.ts, nivel platform) — consulta la BD sin HTTP.
 * SIN providedIn:'root' (ver GET_PAGE_DATA_FN) para que suba al platform.
 */
export const GET_PRODUCTS_CONTRACTED_FN = new InjectionToken<
  () => Promise<ProductContractedRow[]>
>('GET_PRODUCTS_CONTRACTED_FN');

@Injectable({ providedIn: 'root' })
export class ProductsContractedService {
  private platformId = inject(PLATFORM_ID);
  private getProductsContractedFn = inject(GET_PRODUCTS_CONTRACTED_FN, {
    optional: true,
  });
  private http = inject(HttpClient);
  private pendingTasks = inject(PendingTasks);

  async getProductsContracted(): Promise<ProductContractedRow[]> {
    if (isPlatformServer(this.platformId)) {
      if (!this.getProductsContractedFn) return [];
      const done = this.pendingTasks.add();
      try {
        return await this.getProductsContractedFn();
      } finally {
        done();
      }
    }

    try {
      const response = await firstValueFrom(
        this.http.get<ProductsContractedResponse>(
          `${environment.apiUrl}/products-contracted`
        )
      );
      return response.data ?? [];
    } catch (error) {
      console.error('[ProductsContractedService] Error:', error);
      return [];
    }
  }
}
